/**
 * Which list columns draw as a status chip (a coloured dot plus the word, for
 * example "● Offline"), and which colour each word gets. The word is always
 * shown, so colour is never the only signal.
 */

export type StatusTone = 'good' | 'bad' | 'pending' | 'neutral';

/** Columns that are statuses even though their names do not say so. */
const NAMED_STATUS_COLUMNS = new Set(['player_data_is_online', 'location_zone_player_stream_mode']);

/** True for a column a list should draw as a status chip. */
export function isStatusColumn(columnKey: string): boolean {
    const base = columnKey.split('.')[0];
    return NAMED_STATUS_COLUMNS.has(base) || base.endsWith('_status') || base.endsWith('_status_id');
}

const GOOD = new Set(['online', 'active', 'done', 'complete', 'completed', 'playing', 'yes', 'enabled', 'matched', 'success', 'succeeded']);
const BAD = new Set(['offline', 'inactive', 'failed', 'error', 'cancelled', 'canceled', 'disabled', 'no', 'suspended', 'closed']);
const PENDING = new Set(['pending', 'queued', 'running', 'processing', 'waiting', 'unknown', 'retrying', 'paused']);

export function statusTone(word: string): StatusTone {
    const normalized = word.trim().toLowerCase();
    if (GOOD.has(normalized)) return 'good';
    if (BAD.has(normalized)) return 'bad';
    if (PENDING.has(normalized)) return 'pending';
    return 'neutral';
}

/** The word a status chip shows for a raw cell value, or null for an empty cell. */
export function statusWord(columnKey: string, value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (columnKey.split('.')[0] === 'player_data_is_online' && typeof value === 'boolean') {
        return value ? 'Online' : 'Offline';
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const name = record.display_string ?? record.name;
        return typeof name === 'string' && name !== '' ? name : null;
    }
    return String(value);
}
